import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getBrandIdentity, saveBrandIdentity } from '../../services/configService';

const PRESETS = [
  { label: 'Blue',    hex: '#3B82F6' },
  { label: 'Cyan',    hex: '#22D3EE' },
  { label: 'Violet',  hex: '#8B5CF6' },
  { label: 'Green',   hex: '#10B981' },
  { label: 'Orange',  hex: '#F97316' },
  { label: 'Red',     hex: '#EF4444' },
  { label: 'Pink',    hex: '#EC4899' },
  { label: 'Amber',   hex: '#F59E0B' },
];

const DEFAULT_COLOR = PRESETS[0].hex;

function isValidHex(val) {
  return /^#[0-9A-Fa-f]{6}$/.test(val);
}

export default function BrandIdentity() {
  const { user } = useAuth();
  const [activePreset, setActivePreset] = useState(DEFAULT_COLOR);
  const [color, setColor]               = useState(DEFAULT_COLOR);
  const [hexInput, setHexInput]         = useState(DEFAULT_COLOR);
  const [assistantName, setAssistantName] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [darkMode, setDarkMode]         = useState(false);
  const [savedConfig, setSavedConfig]   = useState(null);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState('');
  const colorInputRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    getBrandIdentity()
      .then(({ config }) => {
        if (config) {
          const c = config.brandColor || DEFAULT_COLOR;
          setColor(c); setHexInput(c);
          setActivePreset(PRESETS.find(p => p.hex === c) ? c : 'custom');
          setAssistantName(config.assistantName || '');
          setWelcomeMessage(config.welcomeMessage || '');
          setDarkMode(config.darkMode ?? false);
          setSavedConfig(config);
        }
      })
      .catch(() => setError('Failed to load brand identity settings.'))
      .finally(() => setLoading(false));
  }, [user]);

  const isCustom   = activePreset === 'custom';
  const isDirty    = color !== (savedConfig?.brandColor ?? DEFAULT_COLOR)
    || assistantName !== (savedConfig?.assistantName ?? '')
    || welcomeMessage !== (savedConfig?.welcomeMessage ?? '')
    || darkMode !== (savedConfig?.darkMode ?? false);
  const hexInvalid = !isValidHex(hexInput);

  function selectPreset(hex) {
    setActivePreset(hex);
    setColor(hex);
    setHexInput(hex);
  }

  function openCustomPicker() {
    setActivePreset('custom');
    colorInputRef.current?.click();
  }

  function handlePickerChange(e) {
    const hex = e.target.value;
    setColor(hex);
    setHexInput(hex);
  }

  function handleHexInputFocus() {
    setActivePreset('custom');
  }

  function handleHexInputChange(e) {
    const val = e.target.value.startsWith('#') ? e.target.value : '#' + e.target.value;
    setHexInput(val);
    if (isValidHex(val)) setColor(val);
  }

  async function handleSave() {
    if (hexInvalid) return;
    setError('');
    setSaving(true);
    try {
      const config = { brandColor: color, assistantName, welcomeMessage, darkMode };
      await saveBrandIdentity(config);
      setSavedConfig(config);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const showSaving = saving;
  const showSaved  = !isDirty && !saving && savedConfig !== null;

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#475569', fontSize: 13 }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
        style={{ animation: 'spin 0.8s linear infinite' }}>
        <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0" opacity=".25"/><path d="M21 12a9 9 0 0 0-9-9"/>
      </svg>
      Loading…
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <p style={{ fontSize: 13, color: '#64748b' }}>Give your AI assistant a name, set its opening message, and pick your brand colour.</p>
      {/* Brand Color */}
      <div>
        <p style={{ fontSize: 11.5, color: '#475569', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 8 }}>Brand Color</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          {PRESETS.map(({ hex, label }) => {
            const isActive = activePreset === hex;
            return (
              <button
                key={hex}
                title={label}
                onClick={() => selectPreset(hex)}
                style={{
                  width: 32, height: 32, borderRadius: 8, border: 'none',
                  background: hex, cursor: 'pointer', position: 'relative',
                  outline: isActive ? `3px solid ${hex}` : '3px solid transparent',
                  outlineOffset: 2,
                  boxShadow: isActive ? `0 0 12px ${hex}88` : 'none',
                  transition: 'outline .15s, box-shadow .15s',
                }}
              >
                {isActive && (
                  <svg
                    width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                    style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))' }}
                  >
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                )}
              </button>
            );
          })}

          {/* Custom swatch */}
          <button
            title="Custom colour"
            onClick={openCustomPicker}
            style={{
              width: 32, height: 32, borderRadius: 8, cursor: 'pointer',
              background: isCustom ? color : 'rgba(30,41,59,0.8)',
              border: 'none',
              outline: isCustom ? `3px solid ${isValidHex(color) ? color : '#479EC3'}` : '3px solid rgba(71,158,195,0.25)',
              outlineOffset: 2,
              boxShadow: isCustom && isValidHex(color) ? `0 0 12px ${color}88` : 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'outline .15s, box-shadow .15s, background .15s',
            }}
          >
            {!isCustom && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#479EC3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 8v8M8 12h8" opacity="0.6"/>
              </svg>
            )}
            {isCustom && isValidHex(color) && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))' }}>
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            )}
          </button>

          {/* Hidden native color picker */}
          <input
            ref={colorInputRef}
            type="color"
            value={isValidHex(color) ? color : '#479EC3'}
            onChange={handlePickerChange}
            style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
          />
        </div>

        {/* Hex input */}
        <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Live colour preview */}
          <div style={{
            width: 36, height: 36, borderRadius: 8, flexShrink: 0,
            background: isValidHex(hexInput) ? hexInput : 'rgba(30,41,59,0.8)',
            border: '1px solid rgba(71,158,195,0.2)',
            transition: 'background .1s',
          }} />
          <input
            type="text"
            value={hexInput}
            onChange={handleHexInputChange}
            maxLength={7}
            placeholder="#000000"
            style={{
              padding: '9px 14px', borderRadius: 9, width: 140,
              background: 'rgba(15,23,42,0.7)',
              border: `1.5px solid ${hexInvalid && hexInput !== '' ? 'rgba(239,68,68,0.5)' : 'rgba(71,158,195,0.2)'}`,
              color: hexInvalid && hexInput !== '' ? '#f87171' : '#e2e8f0',
              fontSize: 13.5, fontFamily: "'SF Mono','Fira Code',monospace",
              outline: 'none', letterSpacing: '0.04em',
              transition: 'border-color .15s',
            }}
            onFocus={e => { handleHexInputFocus(); e.target.style.borderColor = hexInvalid ? 'rgba(239,68,68,0.6)' : 'rgba(71,158,195,0.55)'; }}
            onBlur={e => { e.target.style.borderColor = hexInvalid && hexInput !== '' ? 'rgba(239,68,68,0.5)' : 'rgba(71,158,195,0.2)'; }}
          />
          {hexInvalid && hexInput.length > 1 && (
            <span style={{ fontSize: 12, color: '#f87171' }}>Invalid hex</span>
          )}
        </div>
      </div>

      {/* Appearance */}
      <div>
        <p style={{ fontSize: 11.5, color: '#475569', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 8 }}>Appearance</p>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 3, padding: 3,
          background: 'rgba(15,23,42,0.7)', border: '1.5px solid rgba(71,158,195,0.2)', borderRadius: 11,
        }}>
          {[
            { value: false, label: 'Light', icon: (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/>
              </svg>
            ) },
            { value: true, label: 'Dark', icon: (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
            ) },
          ].map(opt => {
            const isActive = darkMode === opt.value;
            return (
              <button
                key={opt.label}
                onClick={() => setDarkMode(opt.value)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7,
                  padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: isActive ? 'linear-gradient(135deg,#479EC3,#22D3EE)' : 'transparent',
                  color: isActive ? '#fff' : '#94a3b8',
                  fontSize: 13, fontWeight: 600, transition: 'background .15s, color .15s',
                }}
              >
                {opt.icon}{opt.label}
              </button>
            );
          })}
        </div>
        <p style={{ fontSize: 11.5, color: '#64748b', marginTop: 8 }}>
          Sets the default theme of your chat widget. Visitors can still switch it themselves.
        </p>
      </div>

      {/* Assistant Name */}
      <div>
        <p style={{ fontSize: 11.5, color: '#475569', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 8 }}>Assistant Name</p>
        <input
          type="text"
          value={assistantName}
          onChange={e => setAssistantName(e.target.value)}
          placeholder="e.g. Aria, Support Bot, Reano AI…"
          maxLength={60}
          style={{
            width: '100%', padding: '9px 14px', borderRadius: 9, boxSizing: 'border-box',
            background: 'rgba(15,23,42,0.7)',
            border: '1.5px solid rgba(71,158,195,0.2)',
            color: '#e2e8f0', fontSize: 13.5, outline: 'none',
            transition: 'border-color .15s',
          }}
          onFocus={e => { e.target.style.borderColor = 'rgba(71,158,195,0.55)'; }}
          onBlur={e => { e.target.style.borderColor = 'rgba(71,158,195,0.2)'; }}
        />
      </div>

      {/* Welcome Message */}
      <div>
        <p style={{ fontSize: 11.5, color: '#475569', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 8 }}>Welcome Message</p>
        <textarea
          value={welcomeMessage}
          onChange={e => setWelcomeMessage(e.target.value)}
          placeholder="e.g. Hi there! How can I help you today?"
          maxLength={300}
          rows={3}
          style={{
            width: '100%', padding: '9px 14px', borderRadius: 9, boxSizing: 'border-box',
            background: 'rgba(15,23,42,0.7)',
            border: '1.5px solid rgba(71,158,195,0.2)',
            color: '#e2e8f0', fontSize: 13.5, outline: 'none', resize: 'none',
            fontFamily: 'Inter, system-ui, sans-serif', lineHeight: 1.6,
            transition: 'border-color .15s',
          }}
          onFocus={e => { e.target.style.borderColor = 'rgba(71,158,195,0.55)'; }}
          onBlur={e => { e.target.style.borderColor = 'rgba(71,158,195,0.2)'; }}
        />
        <p style={{ fontSize: 11.5, color: '#334155', marginTop: 4, textAlign: 'right' }}>{welcomeMessage.length}/300</p>
      </div>

      {error && (
        <p style={{ fontSize: 13, color: '#f87171', margin: 0, padding: '8px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
          {error}
        </p>
      )}

      {/* Save row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={handleSave}
          disabled={!isDirty || saving || hexInvalid}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '10px 24px', borderRadius: 9, border: 'none',
            cursor: !isDirty || saving || hexInvalid ? 'not-allowed' : 'pointer',
            background: !isDirty || saving || hexInvalid ? 'rgba(30,41,59,0.6)' : 'linear-gradient(135deg,#479EC3,#22D3EE)',
            color: !isDirty || saving || hexInvalid ? '#334155' : '#fff',
            fontSize: 14, fontWeight: 700,
            transition: 'background .2s',
          }}
        >
          Save
        </button>

        <span style={{
          fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, minWidth: 72,
          color: showSaving ? '#64748b' : '#10b981',
          opacity: showSaving || showSaved ? 1 : 0,
          transition: 'opacity .2s',
          pointerEvents: 'none',
        }}>
          {showSaving ? (
            <>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ animation: 'spin 0.8s linear infinite', flexShrink: 0 }}>
                <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0" opacity=".25"/><path d="M21 12a9 9 0 0 0-9-9"/>
              </svg>
              Saving…
            </>
          ) : (
            <>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Saved
            </>
          )}
        </span>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
